"use client";

import * as React from "react";
import { Label as LabelPrimitive, Slot as SlotPrimitive } from "radix-ui";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { ParseKeys } from "i18next";
import { useTranslation } from "@/app/i18n/client";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(
  null,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  if (!itemContext) {
    throw new Error("useFormField should be used within <FormItem>");
  }

  const fieldState = getFieldState(fieldContext.name, formState);

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef<
  React.ComponentRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && "text-red-500 dark:text-red-900", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef<
  React.ComponentRef<typeof SlotPrimitive.Slot>,
  React.ComponentPropsWithoutRef<typeof SlotPrimitive.Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <SlotPrimitive.Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn(
        "text-[0.8rem] text-neutral-500 dark:text-neutral-400",
        className,
      )}
      {...props}
    />
  );
});
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : children;

  if (!body) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn(
        "text-[0.8rem] font-medium text-red-500 dark:text-red-900",
        className,
      )}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessage.displayName = "FormMessage";
type NestedError =
  | { message: string; type: string }
  | { [s: string]: NestedError };

const FormNestedMessages = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    translationKeys?: Map<string, ParseKeys<"common">>;
  }
>(({ className, children, translationKeys, ...props }, ref) => {
  const fieldContext = React.useContext(FormFieldContext);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { t } = useTranslation();
  const { errors } = useFormState();

  const rootError = errors[fieldContext.name];

  const getNestedErrs = (e: NestedError) => {
    console.log("NestedError keys: " + JSON.stringify(Object.keys(e)));
    const entries = Object.entries(e).filter(
      ([k, v]) => k !== "ref" && k !== "type" && typeof v === "object",
    );
    const res = entries
      .filter(([k, v]) => typeof v.message === "string")
      .map(([k, v]) => [k, v.message!]);
    entries
      .filter(([k, v]) => typeof v.message === "undefined")
      .forEach(([k, v]) => {
        const r2 = getNestedErrs(v as NestedError);
        console.log("GOT BACK: " + JSON.stringify(r2));
        res.push(...r2);
      });
    console.log("RETURN: " + JSON.stringify(res));
    return res;
  };

  if (rootError) {
    const err1 = rootError.message;
    console.log("Errobject: " + JSON.stringify(rootError as NestedError));
    const nestedErrs = getNestedErrs(rootError as NestedError);
    return (
      <div className={cn("block w-full", className)} {...props} ref={ref}>
        <ul className="m-0 list-none p-0">
          {err1 && (
            <li className="text-[0.8rem] font-medium leading-tight text-red-500 dark:text-red-900">
              {err1.toString()}
            </li>
          )}
          {nestedErrs.map(([k, v]) => (
            <li
              key={`${fieldContext.name}-${k}`}
              className="text-[0.8rem] font-medium leading-tight text-red-500 dark:text-red-900"
            >
              <b>
                {translationKeys
                  ? translationKeys.has(k)
                    ? t(translationKeys.get(k)!)
                    : k
                  : k}
                :
              </b>{" "}
              {v}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (!children) {
    return null;
  }
});
FormNestedMessages.displayName = "FormNestedMessages";

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormNestedMessages,
  FormField,
};
